import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ExternalLink, Clock, CheckCircle2, AlertCircle, Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/orders/$orderId/shopier")({
  head: () => ({ meta: [{ title: "Shopier ile Öde — JUËN" }] }),
  component: ShopierPaymentPage,
});

interface OrderRow {
  id: string;
  user_id: string;
  status: string;
  quantity: number;
  total_kurus: number;
  ticket_type_id: string | null;
  event_id: string;
}

interface PaymentLink {
  name: string;
  url: string;
  qty: number;
  totalKurus: number;
}

function ShopierPaymentPage() {
  const { orderId } = Route.useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderRow | null>(null);
  const [links, setLinks] = useState<PaymentLink[]>([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [openedLinks, setOpenedLinks] = useState<Set<number>>(new Set());
  const [codeCopied, setCodeCopied] = useState(false);

  const orderCode = order ? order.id.slice(0, 8).toUpperCase() : "";

  const [feeKurus, setFeeKurus] = useState(0);
  const ticketTotalKurus = order?.total_kurus ?? 0;
  const totalWithFeeKurus = ticketTotalKurus + feeKurus;

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(orderCode);
      setCodeCopied(true);
      toast.success("Sipariş kodu kopyalandı");
      setTimeout(() => setCodeCopied(false), 2000);
    } catch {
      toast.error("Kopyalanamadı");
    }
  };

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    void (async () => {
      // Load order
      const { data: ord, error } = await supabase
        .from("orders")
        .select("*")
        .eq("id", orderId)
        .maybeSingle();

      if (error || !ord) {
        toast.error("Sipariş bulunamadı");
        void navigate({ to: "/dashboard" });
        return;
      }
      if (ord.user_id !== user.id) {
        toast.error("Bu sipariş size ait değil");
        void navigate({ to: "/dashboard" });
        return;
      }

      setOrder(ord as OrderRow);

      // Load event fee config
      let vatPct = 0;
      let svcFee = 0;
      if (ord.event_id) {
        const { data: ev } = await supabase
          .from("events")
          .select("vat_percent,service_fee_kurus")
          .eq("id", ord.event_id)
          .maybeSingle();
        vatPct = Math.max(0, Number((ev as { vat_percent?: number | string } | null)?.vat_percent ?? 0));
        svcFee = Math.max(0, (ev as { service_fee_kurus?: number } | null)?.service_fee_kurus ?? 0);
      }

      // Load ticket type link + qty
      const collected: PaymentLink[] = [];
      if (ord.ticket_type_id && ord.quantity > 0) {
        const { data: tt } = await supabase
          .from("ticket_types")
          .select("name, price_kurus, shopier_payment_url")
          .eq("id", ord.ticket_type_id)
          .maybeSingle();
        if (tt?.shopier_payment_url) {
          const linkSubtotal = tt.price_kurus * ord.quantity;
          const linkFee = Math.round(linkSubtotal * (vatPct / 100)) + svcFee * ord.quantity;
          collected.push({
            name: tt.name,
            url: tt.shopier_payment_url,
            qty: ord.quantity,
            totalKurus: linkSubtotal + linkFee,
          });
        }
      }

      const orderFee = Math.round(ord.total_kurus * (vatPct / 100)) + svcFee * ord.quantity;
      setFeeKurus(orderFee);
      setLinks(collected);
      setPageLoading(false);
    })();
  }, [orderId, user, navigate]);

  const handleOpenLink = (idx: number, url: string) => {
    const normalized = /^https?:\/\//i.test(url) ? url : `https://${url.replace(/^\/+/, "")}`;
    window.open(normalized, "_blank", "noopener,noreferrer");
    setOpenedLinks((prev) => new Set(prev).add(idx));
  };

  if (loading || !user || pageLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center text-muted-foreground">
          <Loader2 className="mx-auto h-6 w-6 animate-spin" />
        </div>
      </div>
    );
  }

  if (!order) return null;

  if (order.status === "paid") {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-primary" />
          <h1 className="mt-4 text-2xl font-bold">Ödemen tamamlandı</h1>
          <p className="mt-2 text-muted-foreground">Biletlerin hesabında.</p>
          <Button asChild className="mt-6">
            <Link to="/tickets">Biletlerim</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-4 py-10">
        <Link
          to="/dashboard"
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Panele dön
        </Link>

        <h1 className="text-3xl font-bold tracking-tight">Shopier ile Öde</h1>
        <p className="mt-2 text-muted-foreground">
          Sipariş #{order.id.slice(0, 8)} · Toplam {(totalWithFeeKurus / 100).toLocaleString("tr-TR")} ₺
        </p>

        {/* Instructions */}
        <div className="mt-6 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
            <div className="space-y-2 text-sm">
              <p className="font-semibold text-foreground">Nasıl ödeme yapılır?</p>
              <ol className="list-decimal space-y-1 pl-4 text-muted-foreground">
                <li>Aşağıdaki <strong>"Shopier'de Öde"</strong> butonuna bas — yeni sekmede açılır.</li>
                <li>Shopier sayfasında <strong>doğru adedi</strong> seçip <strong>"Hemen Al"</strong> butonuna tıkla; ardından kart bilgilerini girerek ödemeyi güvenli şekilde tamamla.</li>
                <li>
                  Ödeme açıklamasına <strong>sipariş kodunu yaz:</strong>{" "}
                  <span className="inline-flex items-center gap-1.5 align-middle">
                    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-foreground">{orderCode}</code>
                    <button
                      type="button"
                      onClick={handleCopyCode}
                      className="inline-flex h-6 w-6 items-center justify-center rounded border border-border bg-background hover:bg-muted transition-colors"
                      aria-label="Sipariş kodunu kopyala"
                    >
                      {codeCopied ? (
                        <Check className="h-3.5 w-3.5 text-primary" />
                      ) : (
                        <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                    </button>
                  </span>
                </li>
                <li>Ödeme tamamlandıktan sonra siparişin yönetici tarafından kontrol edilip onaylanır.</li>
                <li>Onay sonrası biletlerin hesabında görünür.</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Payment links */}
        <div className="mt-6 space-y-3">
          {links.length === 0 && (
            <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-5 text-sm">
              Bu sipariş için Shopier ödeme linki tanımlı değil. Lütfen yöneticiyle iletişime geç.
            </div>
          )}
          {links.map((link, idx) => {
            const opened = openedLinks.has(idx);
            const linkTotalKurus = link.totalKurus;
            return (
              <div
                key={idx}
                className="flex items-center justify-between gap-4 rounded-2xl border border-border bg-card p-4 shadow-card"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{link.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {link.qty} adet · {(linkTotalKurus / 100).toLocaleString("tr-TR")} ₺
                  </div>
                  <div className="text-xs text-muted-foreground">
                    (Hizmet bedeli dahil)
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={() => handleOpenLink(idx, link.url)}
                  variant={opened ? "outline" : "default"}
                  size="sm"
                  className="flex-shrink-0"
                >
                  {opened ? (
                    <>
                      <CheckCircle2 className="mr-1.5 h-4 w-4" /> Tekrar aç
                    </>
                  ) : (
                    <>
                      Shopier'de Öde <ExternalLink className="ml-1.5 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            );
          })}
        </div>


        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <Button asChild variant="outline" className="flex-1">
            <Link to="/dashboard">Panele dön</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
